import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Toggle from '@risk-smart/themed-cloudscape-components/toggle';
import Button from '@risksmart-app/components/src/button';
import type { ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledInput } from '@/components/form/controlled-input/ControlledInput';
import { ControlledMultiselect } from '@/components/form/controlled-multiselect/ControlledMultiselect';
import type {
  HidableOption,
  HidableOptionGroup,
} from '@/components/form/controlled-multiselect/types';
import ControlledTextarea from '@/components/form/controlled-textarea';
import { useListWebhookEventTypes } from '@/hooks/queries';

import type { WebhookFormFields } from './webhookSchema';
import {
  ALL_EVENTS_BACKEND_KEY,
  ALL_EVENTS_SENTINEL,
  defaultValues as schemaDefaultValues,
  useWebhookSchema,
} from './webhookSchema';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const buildEventTypeOptions = (
  events: { Id: string; EventType: string; Description?: null | string }[]
): (HidableOption | HidableOptionGroup)[] => {
  const groups = new Map<
    string,
    { Id: string; EventType: string; Description?: null | string }[]
  >();
  // The wildcard ('*') row drives the dedicated "All events" mode, not a
  // selectable option, so keep it out of the "Select events" multiselect.
  events
    .filter((event) => event.EventType !== ALL_EVENTS_BACKEND_KEY)
    .forEach((event) => {
      const [prefix] = event.EventType.split('.');
      const key = prefix || 'other';
      const existing = groups.get(key) ?? [];
      existing.push(event);
      groups.set(key, existing);
    });

  return Array.from(groups.entries()).map(
    ([groupName, groupEvents]): HidableOptionGroup => ({
      label: capitalize(groupName),
      options: groupEvents.map((event) => ({
        value: event.Id,
        label: event.EventType,
        description: event.Description ?? undefined,
      })),
    })
  );
};

interface Props {
  defaultValues?: Partial<WebhookFormFields>;
  /**
   * Persisted webhook status from the server. Shown as a label next to the
   * Status toggle so users can see the current state, including 'failed'
   * which is set by the backend and not directly togglable.
   */
  currentStatus?: 'active' | 'failed' | 'paused';
  loading?: boolean;
  onSubmit: (values: WebhookFormFields) => Promise<void> | void;
  renderActions: (form: UseFormReturn<WebhookFormFields>) => ReactNode;
}

export const WebhookForm = ({
  defaultValues,
  currentStatus,
  loading,
  onSubmit,
  renderActions,
}: Props) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'webhooks.form' });
  const { t: stat } = useTranslation(['common'], {
    keyPrefix: 'webhooks.status',
  });
  const schema = useWebhookSchema();
  const form = useForm<WebhookFormFields>({
    // 'onTouched' validates on first blur, then on every change — keeps
    // formState.isValid fresh after a failed submit so the URL field's
    // error clears as the user fixes it.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { ...schemaDefaultValues, ...defaultValues },
  });
  const { control, handleSubmit, setValue, watch } = form;

  const {
    data: eventTypesData,
    loading: eventTypesLoading,
    error: eventTypesError,
  } = useListWebhookEventTypes({ queryArgs: undefined });

  const eventTypes = useMemo(
    () => eventTypesData?.organisation_webhook_event_type ?? [],
    [eventTypesData]
  );
  const eventTypeOptions = useMemo(
    () => buildEventTypeOptions(eventTypes),
    [eventTypes]
  );

  const status = watch('Status');
  const eventMode = watch('EventMode');

  // Wildcard sentinel — covers all current AND future event types. Stored as
  // a single synthetic row in EventTypes when EventMode === 'all'.
  const allEventsLabel = t('events_mode_all');
  const allEventsOption = useMemo(
    () => [{ value: ALL_EVENTS_SENTINEL, label: allEventsLabel }],
    [allEventsLabel]
  );

  // Stash the user-curated selection when they switch to 'all', so it can be
  // restored if they switch back to 'select'.
  const previousSelectionRef = useRef<WebhookFormFields['EventTypes']>(
    form.getValues('EventTypes') ?? []
  );

  const handleEventModeChange = (next: 'all' | 'select') => {
    if (next === eventMode) {
      return;
    }
    if (next === 'all') {
      previousSelectionRef.current = form.getValues('EventTypes') ?? [];
      setValue('EventTypes', allEventsOption, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue('EventTypes', previousSelectionRef.current, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setValue('EventMode', next, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
      >
        <SpaceBetween size={'l'}>
          <ControlledInput
            name={'Title'}
            label={t('title_label')}
            placeholder={t('title_placeholder')}
            control={control}
            disabled={loading}
            forceRequired
          />
          <Box>
            <ControlledInput
              name={'WebhookUrl'}
              label={t('url_label')}
              placeholder={t('url_placeholder')}
              control={control}
              disabled={loading}
              forceRequired
              disableBottomPadding
            />
            <Box variant={'small'} color={'text-status-inactive'}>
              {t('url_description')}
            </Box>
          </Box>
          <ControlledTextarea
            name={'Description'}
            label={t('description_label')}
            placeholder={t('description_placeholder')}
            control={control}
            disabled={loading}
          />
          <FormField
            label={
              currentStatus
                ? `${t('status_label')}: ${stat(currentStatus, {
                    defaultValue: currentStatus,
                  })}`
                : t('status_label')
            }
          >
            <Toggle
              checked={status === 'active'}
              disabled={loading}
              onChange={({ detail }) => {
                setValue('Status', detail.checked ? 'active' : 'paused', {
                  shouldDirty: true,
                });
              }}
            >
              {status === 'active'
                ? t('status_active_description')
                : t('status_paused_description')}
            </Toggle>
          </FormField>
          <FormField
            label={t('events_label')}
            description={t('events_description')}
          >
            <SpaceBetween size={'s'}>
              <SpaceBetween direction={'horizontal'} size={'xs'}>
                <Button
                  variant={eventMode === 'all' ? 'primary' : 'normal'}
                  formAction={'none'}
                  onClick={() => handleEventModeChange('all')}
                  disabled={loading || eventTypesLoading || !!eventTypesError}
                >
                  {t('events_mode_all')}
                </Button>
                <Button
                  variant={eventMode === 'select' ? 'primary' : 'normal'}
                  formAction={'none'}
                  onClick={() => handleEventModeChange('select')}
                  disabled={loading || eventTypesLoading || !!eventTypesError}
                >
                  {t('events_mode_select')}
                </Button>
              </SpaceBetween>
              {eventMode === 'all' && (
                <Alert type={'info'}>{t('events_all_active_message')}</Alert>
              )}
              {eventTypesError ? (
                <Alert type={'error'}>{t('events_load_error_message')}</Alert>
              ) : !eventTypesLoading && eventTypes.length === 0 ? (
                <Alert type={'warning'}>{t('events_empty_message')}</Alert>
              ) : null}
            </SpaceBetween>
          </FormField>
          {eventMode === 'select' && (
            <ControlledMultiselect
              name={'EventTypes'}
              label={t('events_label')}
              control={control}
              options={eventTypeOptions}
              disabled={loading || eventTypesLoading || !!eventTypesError}
              forceRequired
            />
          )}
          {renderActions(form)}
        </SpaceBetween>
      </form>
    </FormProvider>
  );
};

export default WebhookForm;
